import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { QuotesApi } from '../../api/resources';
import { ApiError } from '../../api/client';
import type { Quote, QuoteFormat } from '../../api/types';
import { useToast } from '../../context/ToastContext';
import { QuoteDocument } from '../../components/QuoteDocument';

const MAX_LOGO_BYTES = 500 * 1024;

export function QuoteFormatSettings() {
  const { notify } = useToast();
  const [format, setFormat] = useState<QuoteFormat | null>(null);
  const [logoData, setLogoData] = useState<string | null>(null);
  const [footerText, setFooterText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    QuotesApi.getFormat()
      .then(({ format: f }) => {
        setFormat(f);
        setLogoData(f.logoData);
        setFooterText(f.footerText ?? '');
      })
      .catch((err) => notify(err instanceof ApiError ? err.message : 'No fue posible cargar el formato.', 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      notify('El logotipo debe ser una imagen PNG, JPG o WEBP.', 'error');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      notify('El logotipo no puede superar 500 KB.', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogoData(String(reader.result));
    reader.readAsDataURL(file);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const { format: saved } = await QuotesApi.saveFormat({ logoData, footerText: footerText.trim() || null });
      setFormat(saved);
      notify('Formato de cotización guardado.', 'success');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible guardar el formato.', 'error');
    } finally {
      setSaving(false);
    }
  }

  const previewFormat: QuoteFormat | null = format ? { ...format, logoData, footerText: footerText.trim() || null } : null;
  const previewQuote: Quote = {
    id: 'preview',
    code: 'COT-EJEMPLO',
    clientName: 'Cliente de ejemplo',
    clientDocument: '123456789',
    clientAddress: 'Calle 1 # 2-3',
    clientPhone: '3000000000',
    date: new Date().toISOString(),
    recipeNameSnapshot: '',
    quantity: '1',
    unitProductionCost: '0',
    totalCost: '120000',
    createdAt: new Date().toISOString(),
    items: [
      { id: '1', codeSnapshot: 'ARR-001', nameSnapshot: 'Ramo de rosas', description: 'Docena de rosas rojas', quantity: '2', unitCost: '60000', totalCost: '120000' },
    ],
  };

  return (
    <div>
      <h1>Formato de cotización</h1>
      <p className="text-muted">Define el logotipo y el pie de página que se usarán en las cotizaciones de esta sede. La empresa y la sede se muestran automáticamente.</p>

      <div className="card">
        <form onSubmit={handleSave}>
          <div className="field">
            <label htmlFor="qfLogo">Logotipo de la empresa (PNG, JPG o WEBP, máx. 500 KB)</label>
            <input id="qfLogo" type="file" accept="image/png,image/jpeg,image/webp" onChange={handleFile} />
          </div>
          {logoData && (
            <div className="mt-1">
              <img src={logoData} alt="Logotipo actual" style={{ maxHeight: 90, maxWidth: 240 }} />
              <div>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setLogoData(null)}>
                  Quitar logotipo
                </button>
              </div>
            </div>
          )}
          <div className="field mt-1">
            <label htmlFor="qfFooter">Texto de pie de página (condiciones, vigencia, datos de contacto)</label>
            <textarea id="qfFooter" maxLength={600} value={footerText} onChange={(e) => setFooterText(e.target.value)} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving || !format}>
            {saving ? 'Guardando...' : 'Guardar formato'}
          </button>
        </form>
      </div>

      {previewFormat && (
        <div className="card">
          <h2>Vista previa</h2>
          <QuoteDocument quote={previewQuote} format={previewFormat} />
        </div>
      )}
    </div>
  );
}
